import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { z } from "zod"

import { useUpdateProduct } from "../../hooks/useUpdateProduct"
import type { ProductInfoFormValues } from "../../schemas/productFormSchema"
import { ProductInfoFormSchema } from "../../schemas/productFormSchema"
import { convertToProductInfoFormValues } from "../../utils/productForm"
import { ProductInfoForm } from "../ProductInfoForm"

import { Button } from "@/components/common/Button"
import type { Product } from "@/graphql/codegen/graphql"
import type { LocationAssociation } from "@/screens/Products/components/ProductShipLocationSelect"

interface EditProductInfoDialogProps {
  product: Product
  onClose: () => void
  onSubmit: () => void
}

const EditProductInfoDialog: FC<EditProductInfoDialogProps> = ({ product, onClose, onSubmit }) => {
  const methods = useForm<ProductInfoFormValues>({
    defaultValues: convertToProductInfoFormValues(product),
    resolver: zodResolver(
      ProductInfoFormSchema.superRefine((values, context) => {
        if (product.locationsAssociations?.length > 0 && values.locationsAssociations?.length === 0) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "Cannot remove all locations once they have been set. You can only update them to different locations.",
            path: ["locationsAssociations"],
          })
        }
      })
    ),
    mode: "all",
  })
  const {
    formState: { isDirty, isValid, isSubmitting },
    handleSubmit,
  } = methods
  const canSubmit = isDirty && isValid && !isSubmitting
  const { update } = useUpdateProduct({ onSuccess: onSubmit })

  const submitForm = async (formValues: ProductInfoFormValues) => {
    const { locationsAssociations, ...rest } = formValues
    await update({
      ...rest,
      productId: product.productId,
      locationsAssociations: (locationsAssociations as LocationAssociation[]).map(({ locationAssociationId }) => ({
        locationAssociationId,
      })),
    })
  }

  return (
    <Dialog open onClose={onClose} fullWidth>
      <DialogTitle className='pl-10 font-normal text-primary'>Edit Product Information</DialogTitle>
      <IconButton aria-label='close' onClick={onClose} className='absolute right-4 top-3 text-primary'>
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent classes={{ root: "p-10" }}>
        <FormProvider {...methods}>
          <ProductInfoForm />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!canSubmit} onClick={handleSubmit(submitForm)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditProductInfoDialog
